import produce from "immer";
import { createAction, createAsyncAction } from "typesafe-actions";
import { ActionType, createReducer } from "typesafe-actions";
import { ToDo, APICallStatus } from "../../../Types/UnitFeatureTypes";


interface ToDoRequestState {
  request: {
    loading: boolean;
    error: string | null;

    createTaskAPIStatus: APICallStatus;
    updateTaskAPIStatus: APICallStatus;
    updateTaskStatusAPIStatus: APICallStatus;
  };
}

export enum Types {
  CREATE_TASK_REQUEST = 'CREATE_TASK_REQUEST',
  CREATE_TASK_SUCCESS = 'CREATE_TASK_SUCCESS',
  CREATE_TASK_FAILURE = 'CREATE_TASK_FAILURE',

  UPDATE_TASK_REQUEST = 'UPDATE_TASK_REQUEST',
  UPDATE_TASK_SUCCESS = 'UPDATE_TASK_SUCCESS',
  UPDATE_TASK_FAILURE = 'UPDATE_TASK_FAILURE',

  UPDATE_TASK_STATUS_REQUEST = 'UPDATE_TASK_STATUS_REQUEST',
  UPDATE_TASK_STATUS_SUCCESS = 'UPDATE_TASK_STATUS_SUCCESS',
  UPDATE_TASK_STATUS_FAILURE = 'UPDATE_TASK_STATUS_FAILURE',

  RESET_STATUS_REQUEST = 'RESET_STATUS_REQUEST',
}

const createTaskRequestAction = createAsyncAction(
  Types.CREATE_TASK_REQUEST,
  Types.CREATE_TASK_SUCCESS,
  Types.CREATE_TASK_FAILURE,
)
<
  { toDoData: ToDo },
  { success: boolean },
  { message: string }
>();

const updateTaskRequestAction = createAsyncAction(
  Types.UPDATE_TASK_REQUEST,
  Types.UPDATE_TASK_SUCCESS,
  Types.UPDATE_TASK_FAILURE,
)
<
  { toDoData: ToDo },
  { success: boolean },
  { message: string }
>();

const updateTaskStatusRequestAction = createAsyncAction(
  Types.UPDATE_TASK_STATUS_REQUEST,
  Types.UPDATE_TASK_STATUS_SUCCESS,
  Types.UPDATE_TASK_STATUS_FAILURE,
)
<
  { status: string, id: number },
  { success: boolean },
  { message: string }
>();

const resetStatusRequestAction = createAction(Types.RESET_STATUS_REQUEST)();

export const Actions = { createTaskRequestAction, updateTaskRequestAction, updateTaskStatusRequestAction, resetStatusRequestAction };

export const initialState: ToDoRequestState = {
  request: {
    loading: false,
    error: null as string | null,
    createTaskAPIStatus: APICallStatus.PENDING,
    updateTaskAPIStatus: APICallStatus.PENDING,
    updateTaskStatusAPIStatus: APICallStatus.PENDING,
  }
};

export type ToDoRequestInfoState = typeof initialState;

const createTaskRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.createTaskAPIStatus = APICallStatus.PENDING;

    return draft;
  });

const createTaskRequestSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.createTaskAPIStatus = APICallStatus.SUCCESS;
    if (draft.request.createTaskAPIStatus) {
      draft.request.loading = false;
    }

    return draft;
  });

const createTaskRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.createTaskRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.createTaskAPIStatus = APICallStatus.FAILURE;
    draft.request.error = payload.message;
  });

const updateTaskRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.updateTaskAPIStatus = APICallStatus.PENDING;

    return draft;
  });

const updateTaskRequestSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.updateTaskAPIStatus = APICallStatus.SUCCESS;
    if (draft.request.updateTaskAPIStatus) {
      draft.request.loading = false;
    }

    return draft;
  });

const updateTaskRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.updateTaskRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.updateTaskAPIStatus = APICallStatus.FAILURE;
    draft.request.error = payload.message;
  });

const updateTaskStatusRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.updateTaskStatusAPIStatus = APICallStatus.PENDING;

    return draft;
  });

const updateTaskStatusRequestSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.updateTaskStatusAPIStatus = APICallStatus.SUCCESS;
    if (draft.request.updateTaskStatusAPIStatus) {
      draft.request.loading = false;
    }

    return draft;
  });

const updateTaskStatusRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.updateTaskStatusRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.updateTaskStatusAPIStatus = APICallStatus.FAILURE;
    draft.request.error = payload.message;
  });

const resetStatusRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = null;
    draft.request.createTaskAPIStatus = APICallStatus.PENDING;
    draft.request.updateTaskAPIStatus = APICallStatus.PENDING;
    draft.request.updateTaskStatusAPIStatus = APICallStatus.PENDING;

    return draft;
  });

export default createReducer<typeof initialState, ActionType<typeof Actions>>(
  initialState
)
  .handleAction(Actions.createTaskRequestAction.request, createTaskRequestRequest)
  .handleAction(Actions.createTaskRequestAction.success, createTaskRequestSuccess)
  .handleAction(Actions.createTaskRequestAction.failure, createTaskRequestFailure)
  .handleAction(Actions.updateTaskRequestAction.request, updateTaskRequestRequest)
  .handleAction(Actions.updateTaskRequestAction.success, updateTaskRequestSuccess)
  .handleAction(Actions.updateTaskRequestAction.failure, updateTaskRequestFailure)
  .handleAction(Actions.updateTaskStatusRequestAction.request, updateTaskStatusRequestRequest)
  .handleAction(Actions.updateTaskStatusRequestAction.success, updateTaskStatusRequestSuccess)
  .handleAction(Actions.updateTaskStatusRequestAction.failure, updateTaskStatusRequestFailure)
  .handleAction(Actions.resetStatusRequestAction, resetStatusRequest);
  